/**
 * Chapter demo shell: one merged gradient header (nav eyebrow + page title + personalization +
 * status pills + actions) plus optional Nova message bar.
 */
import { useLocation } from 'react-router-dom';
import { btnPrimary, introCardKeyline, demoChapterStackColumnStyle } from '../designSystem';
import { NovaUnifiedPageBar, novaContextPillFromRaw } from './NovaUnified';
import '../styles/pageHeaders.css';

const FONT = '"Plus Jakarta Sans", -apple-system, sans-serif';

const DOT_COLORS = {
  'bg-rose-300': '#fda4af',
  'bg-rose-400': '#fb7185',
  'bg-teal-400': '#2dd4bf',
  'bg-pink-400': '#f472b6',
  'bg-violet-400': '#a78bfa',
  'bg-amber-400': '#fbbf24',
  'bg-orange-400': '#fb923c',
  'bg-emerald-400': '#34d399',
  'bg-mint-400': '#6BB9A8',
  'bg-gray-300': '#d1d5db',
};

function resolveDotColor(dotColor) {
  if (!dotColor) return '#94a3b8';
  return DOT_COLORS[dotColor] || (dotColor.startsWith('#') || dotColor.startsWith('var(') ? dotColor : '#94a3b8');
}

function themeToPalette(theme) {
  if (!theme) return null;
  return {
    primary: theme.accent,
    primaryL: theme.accentBg || theme.accent + '22',
    primaryM: theme.accent + '88',
    primaryDk: theme.accentText || theme.accent,
  };
}

function normalizeSpace(s) {
  return String(s || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function filterPillsForTrailingDuplicate(pills, pillText) {
  if (!pills?.length) return [];
  const t = normalizeSpace(pillText);
  if (!t) return pills;
  return pills.filter((p) => normalizeSpace(p.label) !== t);
}

export default function ChapterShellPageHeader({
  pageTitle = 'My Journey',
  chapter,
  /** @type {'fertility'|'ttc'} */
  headerChapter = 'fertility',
  eyebrow,
  theme,
  statusPill,
  statusPillText,
  subtitle,
  statusCard,
  novaMessage,
  onSignUp,
  palette,
  hideStatusCard = false,
  hideNovaMessage = false,
  gradientEnd,
  /** Replace default status pill + Join CTA (e.g. TTC My Cycle actions) */
  trailing,
}) {
  const { pathname } = useLocation();
  const headerStatValueFontSize = pathname.startsWith('/demo/') ? 16 : 26;
  const resolvedPalette = palette || (theme ? themeToPalette(theme) : null) || {
    primary: 'var(--pink)',
    primaryL: 'var(--pink-l)',
    primaryM: 'var(--pink-m)',
    primaryDk: 'var(--pink-d)',
  };
  const statusGradientEnd = gradientEnd ?? theme?.gradientEnd ?? 'var(--sage-l)';
  const pillText = statusPillText ?? statusPill;
  const P = resolvedPalette.primary;
  const PL = resolvedPalette.primaryL;
  const PM = resolvedPalette.primaryM;
  const PD = resolvedPalette.primaryDk;

  const explicitCard = !hideStatusCard && statusCard ? statusCard : null;
  const bodyDescriptor = explicitCard ? explicitCard.subtitle || subtitle : subtitle;
  const displayPills = filterPillsForTrailingDuplicate(explicitCard?.pills, pillText);

  const chapterClass = headerChapter === 'ttc' ? 'chapter-ttc' : 'chapter-fertility';
  const emoji = headerChapter === 'ttc' ? '🤍' : '🌸';
  const showEyebrow = eyebrow != null && String(eyebrow).trim() !== '';

  const defaultTrailing = (
    <>
      {pillText ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            fontWeight: 600,
            color: PD,
            background: PL,
            border: `1px solid ${PM}`,
            borderRadius: 999,
            padding: '5px 12px',
          }}
        >
          {/* Match StagePill on /demo/fertility/myjourney — chapter icon before cycle line */}
          {headerChapter === 'fertility' ? (
            <span aria-hidden style={{ lineHeight: 1 }}>
              {emoji}
            </span>
          ) : null}
          <span>{pillText}</span>
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => onSignUp?.('/signup')}
        style={{
          ...btnPrimary,
          fontFamily: FONT,
          background: P,
        }}
      >
        Join the waitlist
      </button>
    </>
  );

  const actions = trailing ?? defaultTrailing;

  return (
    <div style={demoChapterStackColumnStyle}>
      <div
        style={{
          background: `linear-gradient(140deg, ${PL}, ${statusGradientEnd})`,
          ...introCardKeyline,
          padding: '22px 26px',
          borderRadius: 'var(--r, 14px)',
        }}
      >
        <div className={`page-header-block ${chapterClass} page-header-block--section-stack page-header-block--merged-gradient`}>
          <div className="page-title-card page-title-card--gradient-merged">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px 20px',
                width: '100%',
              }}
            >
              <div className="page-title-card__main" style={{ flex: '1 1 220px', minWidth: 0 }}>
                {showEyebrow ? (
                  <div className="page-eyebrow">
                    <span aria-hidden>{emoji}</span>
                    {' '}
                    <span>{eyebrow}</span>
                  </div>
                ) : null}
                <h1 className="page-title">{pageTitle}</h1>
                {explicitCard?.title ? <p className="page-personalized-line">{explicitCard.title}</p> : null}
                {bodyDescriptor ? <p className="page-descriptor">{bodyDescriptor}</p> : null}
                {displayPills.length > 0 ? (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                    {displayPills.map((p, i) => (
                      <span
                        key={i}
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          fontFamily: FONT,
                          padding: '2px 9px',
                          borderRadius: 999,
                          background: 'rgba(255,255,255,0.72)',
                          color: PD,
                          border: `0.5px solid ${PM}`,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        {p.dot && (
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: resolveDotColor(p.dotColor),
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {p.emoji && <span>{p.emoji}</span>}
                        {p.label}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              <div
                className="page-title-card__actions"
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {actions}
                </div>
                {explicitCard?.stat && !explicitCard?.statCallout ? (
                  <div
                    style={{
                      fontFamily: FONT,
                      padding: '12px 18px',
                      background: 'rgba(255,255,255,0.65)',
                      border: `0.5px solid ${PM}`,
                      borderRadius: 12,
                      textAlign: 'center',
                      minWidth: 100,
                    }}
                  >
                    <div style={{ fontFamily: FONT, fontSize: headerStatValueFontSize, fontWeight: 600, color: 'var(--text)', lineHeight: 1.1 }}>
                      {explicitCard.stat.value}
                    </div>
                    <div style={{ fontFamily: FONT, fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{explicitCard.stat.label}</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {!hideNovaMessage && novaMessage && (
        <NovaUnifiedPageBar
          layout="stacked"
          contextPill={novaContextPillFromRaw(novaMessage.context)}
          body={novaMessage.message}
          actionLabel="Open Nova →"
          onAction={() => onSignUp?.('/nova') || onSignUp?.()}
        />
      )}
    </div>
  );
}
